import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import { UserService } from '../../Services/user.service';
import { ToolsBoxService } from '../../Services/tools.service';
import {FirstLetterPipe} from "../../Pipes/firstletter.pipe";
import {Setting, User} from "../../Models/user.model";
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { NotificationComponent } from '../../Notification/notification.component';
import { TrigrammePipe } from '../../Pipes/trigramme.pipe';
import { switchMap, of } from 'rxjs';

@Component({
    selector: 'settings',
    templateUrl: 'settings.component.html',
    styleUrls: ['settings.component.css'],
    imports: [ReactiveFormsModule, FormsModule, NgClass, NotificationComponent, TrigrammePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsComponent implements OnInit {
    currentUser: User;
    newPassword = '';
    confirmNewPassword = '';
    showAlert = false;

    //Notification
    notifType: string;
    notifMessage: string;

    constructor(private userService: UserService, private toolsBoxService: ToolsBoxService,
        private router: Router, private cdr: ChangeDetectorRef) {
      this.currentUser = new User(Number(this.toolsBoxService.getStoredUserId()));
    }

    ngOnInit() {
        this.toolsBoxService.changeHeaderName("Profile settings");
        this.toolsBoxService.tellChildContainsTabs(false);
        this.retrieveUserInfo();
    }

    retrieveUserInfo = () => {
        this.toolsBoxService.tellActionIsInProgress(true);
      this.userService.getUserInformation(this.currentUser).subscribe(
        (data) => {
          this.currentUser = data;
          this.toolsBoxService.tellActionIsInProgress(false);
          this.cdr.markForCheck();
        },
        (err) => {
          this.toolsBoxService.tellActionIsInProgress(false);
          this.router.navigate(["Error", {errMessage: err.message }]);
        }
      );
    }

  updateData = () => {
        this.toolsBoxService.tellActionIsInProgress(true);
        this.userService.updateUserInformation(this.currentUser, '').pipe(
            switchMap(() => this.newPassword
                ? this.userService.updateUserPwd(this.currentUser.userLogin, this.newPassword)
                : of(true))
        ).subscribe(
            () => {
                this.toolsBoxService.tellActionIsInProgress(false);
                this.notifMessage = 'Your account has been updated';
                this.notifType = 'info';
                this.showAlert = true;
                this.newPassword = '';
                this.confirmNewPassword = '';
                this.toolsBoxService.changeAvatar(this.currentUser.avatar);
                this.cdr.markForCheck();
            },
            (err) => {
                this.toolsBoxService.tellActionIsInProgress(false);
                this.router.navigate(["Error", {errMessage: err.message }]);
            }
        );
    }

    cancel = () => {
        this.retrieveUserInfo();
        this.confirmNewPassword = '';
        this.newPassword = '';
        this.notifMessage = 'Your changes have been discarded';
        this.notifType = 'warning';
        this.showAlert = true;

    }

  getStyle = (userName: string) : string => {
    const firstLetterPipe = new FirstLetterPipe();
    return "user-first-initial-" + firstLetterPipe.transform(userName);
  }

  get monthlyTimesheet(): boolean {
    return this.currentUser.settings?.[Setting.ENABLE_MONTHLY_TIMESHEET] === 'true';
  }
  set monthlyTimesheet(value: boolean) {
    this.currentUser.settings ??= {};
    this.currentUser.settings[Setting.ENABLE_MONTHLY_TIMESHEET] = String(value);
  }

  get timesheetApprovedBy(): string {
    return this.currentUser.settings?.[Setting.TIMESHEETS_APPROVED_BY] ?? '';
  }
  set timesheetApprovedBy(value: string) {
    this.currentUser.settings ??= {};
    this.currentUser.settings[Setting.TIMESHEETS_APPROVED_BY] = value;
  }

  removeAvatar = () => {
      this.currentUser.avatar = null;
  }
  /**
   * Get the selected file and check its size
   * @param event
   */
  onChangeAvatar = (event: any) => {
      let files: File[];
      const target = event.target || event.srcElement;
      files = target.files;
      const currentFile : File  = files[0];

      if (currentFile.size > 60000){
        this.notifType = 'error';
        this.notifMessage = 'The file should size less than 60Kb';
        this.showAlert = true;
        this.cdr.markForCheck();
      }else {
        this.checkFile(currentFile);
      }
  }

  checkFile = (file: File) =>{
      const fileReader = new FileReader();
      //Define the onLoad action
      fileReader.onloadend = () => {

        const imageBase64 = fileReader.result;
        //Check file type: depending on the file extension
        if (!imageBase64.toString().startsWith("data:image")) {
            this.notifMessage = 'Wrong file type';
            this.notifType = 'error';
            this.showAlert = true;
            this.cdr.markForCheck();
        } else {
          //Check picture size
          const img = new Image();
          //Define onLoad action
          img.onload = () => {
            this.currentUser.avatar = imageBase64.toString();
          }
          //Load the file in order to trigger the load action
          img.src = imageBase64.toString();
        }
      }
      //Load the file as a DataURL
      fileReader.readAsDataURL(file);
  }
}
